import { ReservationAmountDiscountSource, VoucherType } from '@cfixe-monorepo/shared';
import type { BookingAmounts, BookingLabels } from '../types';

type VoucherLine = NonNullable<BookingAmounts['voucher_lines']>[number];
type DiscountLine = NonNullable<BookingAmounts['discount_lines']>[number];

/** Gabarits d'une ligne de bon (`{code}` remplacé) : carte cadeau, forfait, bon de prestation. */
export interface VoucherLineTemplates {
  amount: string;
  pack: string;
  prestation: string;
}

/** Libellé d'une ligne de bon du bloc `amounts`, selon sa nature (récap du tunnel, page de gestion). */
export function voucherLineLabel(line: VoucherLine, templates: VoucherLineTemplates): string {
  const template =
    line.voucher_type === VoucherType.PACK
      ? templates.pack
      : line.voucher_type === VoucherType.PRESTATION
        ? templates.prestation
        : templates.amount;
  return template.replace('{code}', line.code);
}

/** Gabarits d'une ligne de remise : code promo (`{label}` remplacé), remise accordée par le pro. */
export interface DiscountLineTemplates {
  promo: string;
  manual: string;
}

/** Libellé d'une ligne de remise du bloc `amounts` (récap du tunnel, page de gestion). */
export function discountLineLabel(line: DiscountLine, templates: DiscountLineTemplates): string {
  return line.kind === ReservationAmountDiscountSource.MANUAL
    ? templates.manual
    : templates.promo.replace('{label}', line.label);
}

/** Ligne de montant libellée du récap (montant signé serveur). */
export interface AmountRow {
  label: string;
  value: string;
}

type AmountLineLabels = Pick<
  BookingLabels,
  'discount_line' | 'manual_discount_line' | 'voucher_line_amount' | 'voucher_line_pack' | 'voucher_line_prestation'
>;

/** Lignes de remise du bloc `amounts`, au-dessus du total (récap du tunnel, confirmation). */
export const discountRowsOf = (amounts: BookingAmounts | undefined, labels: AmountLineLabels): AmountRow[] =>
  (amounts?.discount_lines ?? []).map(
    (line): AmountRow => ({
      label: discountLineLabel(line, { promo: labels.discount_line, manual: labels.manual_discount_line }),
      value: line.amount,
    }),
  );

/** Lignes de carte cadeau, forfait ou bon du bloc `amounts`, sous le total (récap du tunnel, confirmation). */
export const voucherRowsOf = (amounts: BookingAmounts | undefined, labels: AmountLineLabels): AmountRow[] =>
  (amounts?.voucher_lines ?? []).map(
    (line): AmountRow => ({
      label: voucherLineLabel(line, {
        amount: labels.voucher_line_amount,
        pack: labels.voucher_line_pack,
        prestation: labels.voucher_line_prestation,
      }),
      value: line.amount,
    }),
  );
